/**
 * Shared admin DTOs.
 *
 * The admin frontend (apps/admin) and backend (apps/backend/src/modules/admin)
 * exchange these shapes. Defining them once here prevents the kind of drift
 * caught in PR #97 (admin redefining DB enum values inline; rename in backend
 * silently breaks admin).
 *
 * Backend service interfaces (e.g. AdminPaymentListItem) are structurally
 * compatible with these and can use them directly. If the backend's internal
 * shape diverges from the wire shape, adapt at the controller boundary.
 */

// ---------- Domain enums ----------

import type { UserType } from "./users";

// AdminUserType is the same set as UserType — kept as an alias so admin
// callers reading the type can choose the more domain-specific name.
// Single source of truth lives in users.ts; this prevents the drift caught
// in PR #107 where 'realtor' was added to UserType but missed here.
export type AdminUserType = UserType;

export type EscrowStatus =
	| "pending"
	| "captured"
	| "held"
	| "released"
	| "refunded"
	| "partially_refunded"
	| "failed";

export type DisputeStatus =
	| "open"
	| "under_review"
	| "resolving"
	| "resolved_release"
	| "resolved_refund"
	| "resolved_partial";

/** The outcome a dispute resolution was started with; `partial_refund` is a split. */
export type DisputeResolutionOutcome =
	| "release"
	| "full_refund"
	| "partial_refund";

/**
 * Where one money leg of a dispute resolution stands, as the backend's
 * `dispute_resolution_leg_state` enum records it.
 */
export type DisputeResolutionLegState =
	| "not_started"
	| "pending"
	| "unconfirmed"
	| "failed"
	| "succeeded";

/**
 * The permissions each dispute resolution outcome needs, beyond
 * `admin:payments:dispute:write`. The admin app hides an outcome whose
 * permissions the operator lacks. The backend charges its own copy,
 * `RESOLUTION_OUTCOME_PERMISSIONS` in `dispute-resolution-permissions.ts`,
 * which `satisfies` this one's type, so the two cannot differ and type-check.
 */
export const DISPUTE_RESOLUTION_OUTCOME_PERMISSIONS = {
	release: ["admin:payments:release"],
	full_refund: ["admin:payments:refund"],
	partial_refund: ["admin:payments:release", "admin:payments:refund"],
} as const satisfies Record<DisputeResolutionOutcome, readonly string[]>;

export type DisputeReason =
	| "incomplete_work"
	| "poor_quality"
	| "wrong_materials"
	| "other";

export type ProfileReviewStatus = "pending" | "approved" | "rejected";

export type PortfolioReviewStatus =
	| "draft"
	| "pending_review"
	| "approved"
	| "rejected";

// Kept in step with PayeeResolution in apps/backend/src/modules/admin/admin-payments.service.ts.
// `top_up` is a top-up row: money the homeowner added, with no payee.
export type PayeeResolution =
	| "direct"
	| "designer_fallback"
	| "missing"
	| "top_up";

// ---------- Bootstrap ----------

export interface AdminBootstrap {
	operator: {
		id: string;
		phone: string | null;
		userType: AdminUserType | null;
	};
	authorization: {
		isAdmin: true;
		permissions: string[];
	};
}

// ---------- Audit log ----------

export interface AdminActionLog {
	id: string;
	actorUserId: string | null;
	actorUserPhoneSnapshot: string;
	action: string;
	subjectType: string;
	subjectId: string;
	subjectUserId: string | null;
	reason: string | null;
	metadata: Record<string, unknown>;
	createdAt: string;
}

// ---------- Payments ----------

export interface AdminPaymentDispute {
	id: string;
	transactionId: string;
	reason: DisputeReason;
	details?: string | null;
	status: DisputeStatus;
	resolution?: string | null;
	/** A split's or full refund's refund to the homeowner, in rupees. */
	refundAmount?: number | null;
	/** The recorded intent of a resolution; null when none is in progress or recorded. */
	resolutionOutcome?: DisputeResolutionOutcome | null;
	/** Rupees the resolution pays the designer. */
	resolutionPayoutAmount?: number | null;
	/** Null when the outcome pays the designer nothing. */
	resolutionPayoutState?: DisputeResolutionLegState | null;
	/** Null when the outcome refunds the homeowner nothing. */
	resolutionRefundState?: DisputeResolutionLegState | null;
}

export interface AdminPaymentRecord {
	transaction: {
		id: string;
		projectId: string;
		milestoneId: string | null;
		payerId: string;
		payeeId: string | null;
		amount: number;
		payeeAmount: number;
		status: EscrowStatus;
		providerOrderId?: string | null;
		providerPaymentId?: string | null;
		providerTransferId?: string | null;
		/**
		 * The escrow row's `metadata`. Its `type` is `"top_up"` on a top-up and
		 * `"contractor_assignment"` on a hired contractor's hold.
		 */
		metadata?: Record<string, unknown> | null;
		createdAt: string;
	};
	project: { id: string; title: string };
	milestone: {
		id: string | null;
		title: string | null;
		/** Null on a row with no milestone. */
		status?: "pending" | "completed" | null;
		/** Null on a row with no milestone. */
		fundingStatus?:
			| "unfunded"
			| "funded"
			| "released"
			| "disputed"
			| "refunded"
			| null;
	};
	payer: { id: string; phone: string | null };
	payee: {
		id: string | null;
		phone: string | null;
		resolution?: PayeeResolution;
	};
	payout: {
		payoutReady: boolean;
		blockers: string[];
		accountId: string | null;
		providerAccountId: string | null;
		/**
		 * True when the payee's account was registered by the stub payment
		 * provider while another provider is active; the payment detail then
		 * offers to re-sync it.
		 */
		staleStubAccount?: boolean;
	};
	latestDispute: AdminPaymentDispute | null;
	/**
	 * A second payment for a milestone another payment already funded. It is
	 * never released; it is refunded through a dispute resolved with a full
	 * refund.
	 */
	duplicateCharge: boolean;
	auditLogs?: AdminActionLog[];
}

// ---------- Users ----------

export interface AdminUserRecord {
	user: {
		id: string;
		phone: string | null;
		userType: AdminUserType | null;
		isActive: boolean;
		createdAt: string;
	};
	profile: {
		id?: string;
		name?: string;
		reviewStatus?: ProfileReviewStatus;
		bio?: string | null;
	} | null;
	projectCount: number;
	paymentCount: number;
	auditLogs?: AdminActionLog[];
}

// ---------- Approvals (designer + contractor + realtor review queue) ----------

/*
 * These are URL segments as well as a discriminator: the admin approvals
 * service builds `/admin/{type}/pending`, `/admin/{type}/{id}/verify` and
 * `/admin/{type}/{id}/reject` from the value, and reads the list back from a
 * response key of the same name. All three backend queues answer that shape,
 * so a queue is added here rather than by writing three more service calls.
 */
export type ApprovalType = "designers" | "contractors" | "realtors";

export interface ReviewableProfile {
	id: string;
	userId: string;
	name: string;
	reviewStatus: ProfileReviewStatus;
	reviewNotes?: string | null;
	bio?: string | null;
	profilePictureUrl?: string | null;
	createdAt: string;
}

/**
 * A realtor's full profile, as the realtors queue (`/admin/realtors/pending`)
 * and a realtor's user detail (`/admin/users/{id}`) answer it. Both responses
 * also carry `idProofUrl`, left out of this type on
 * purpose: an uploaded file's URL points at the storage proxy, which serves it
 * only to its owner, so the admin app reads the file through
 * `/admin/realtors/{id}/id-proof` instead.
 */
export interface ReviewableRealtorProfile extends ReviewableProfile {
	agencyName?: string | null;
	reraNumber?: string | null;
	serviceCities?: string[] | null;
	serviceLocalities?: string[] | null;
	yearsExperience?: number | null;
	reraVerified: boolean;
	idVerified: boolean;
	idProofUploaded: boolean;
}

// ---------- Portfolio review queue ----------

/*
 * Not an ApprovalType: portfolios are served from their own endpoints,
 * `/admin/portfolios/pending`, `/admin/portfolios/{id}`, and
 * `/admin/portfolios/{id}/verify` and `/reject`, which answer under
 * `portfolios`, `portfolio` + `projects`, and `portfolio` respectively.
 */
export interface ReviewablePortfolio {
	id: string;
	title: string | null;
	slug: string | null;
	template: string;
	reviewStatus: PortfolioReviewStatus;
	reviewNotes: string | null;
	createdAt: string;
	updatedAt: string;
	/** When the portfolio last entered review; the queue is ordered by this, oldest first. */
	submittedAt: string | null;
	designer: {
		id: string;
		userId: string;
		name: string;
		firmName: string | null;
		profilePictureUrl: string | null;
	};
	projectCount: number;
	/**
	 * The first project's thumbnail by display order; null with no projects.
	 * An uploaded image points at `/api/v1/admin/portfolios/{id}/img/...`, which
	 * authenticates with the admin's cookie like any other admin request.
	 */
	coverUrl: string | null;
}

export interface ReviewablePortfolioProject {
	id: string;
	title: string;
	shortDescription: string;
	fullDescription: string | null;
	category: string | null;
	completionDate: string | null;
	/** Uploaded images point at the admin portfolio image route, as `coverUrl` does. */
	thumbnailUrl: string;
	imageUrls: string[];
	displayOrder: number;
}

/** `GET /admin/portfolios/{id}` */
export interface ReviewablePortfolioDetail {
	portfolio: ReviewablePortfolio;
	projects: ReviewablePortfolioProject[];
}
